<template>
  <div class="comp-wrapper">
    
    <!-- HEADER -->
    <div class="comp-header">
      <div>
        <h4 class="comp-title">Compensation Intelligence</h4>
        <p class="comp-subtitle">Define your organization's job evaluation factors, grade scale, and salary bands.</p>
      </div>
    </div>

    <!-- 2-STEP NAVIGATION -->
    <div class="setup-tabs">
      <div :class="['setup-tab', step === 1 ? 'active' : '']" @click="step = 1">
        <span class="step-num">1</span>
        <div class="step-text">
          <strong>Job Grades</strong>
          <span>Scale & positions</span>
        </div>
      </div>
      <div :class="['setup-tab', step === 2 ? 'active' : '']" @click="step = 2">
        <span class="step-num">2</span>
        <div class="step-text">
          <strong>Salary Bands</strong>
          <span>Min/Max currency ranges</span>
        </div>
      </div>
    </div>

    <!-- ==========================================
         STEP 1: JOB GRADES FRAMEWORK
         ========================================== -->
    <div v-if="step === 1" class="step-content animate-fade-in">
      <div class="comp-box">
        <div class="comp-box-header">
          <h5>Job Grade Scale</h5>
        </div>
        <p class="comp-desc mb-6">
          A grade expresses relative job value based on the 1-5 score from the Evaluation Criteria. It places the job at a percentage inside the salary band.
        </p>

        <div class="grades-table-wrapper">
          <table class="grades-table">
            <thead>
              <tr>
                <th>Grade Label</th>
                <th>Min. Score</th>
                <th>Band Position (%)</th>
                <th>Meaning / Description</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(grade, index) in grades" :key="index">
                <td><input type="text" v-model="grade.id" class="comp-input font-bold" placeholder="e.g. G1" /></td>
                <td><input type="number" v-model.number="grade.minScore" class="comp-input" placeholder="0.0" step="0.1" /></td>
                <td>
                  <div class="input-with-suffix">
                    <input type="number" v-model.number="grade.position" class="comp-input font-bold text-green-700" placeholder="20" />
                    <span class="suffix">%</span>
                  </div>
                </td>
                <td><input type="text" v-model="grade.meaning" class="comp-input w-full" placeholder="Entry value..." /></td>
                <td>
                  <button @click="removeGrade(index)" class="comp-del-btn" title="Remove Grade">
                    <vs-icon icon="trash-2" icon-pack="feather" size="14px"></vs-icon>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="flex justify-between items-center mt-4">
          <button @click="addGrade" class="comp-btn-outline">+ Add Grade</button>
          <button @click="saveGrades" class="comp-btn-primary">
            {{ isSavingGrades ? 'Saving...' : 'Save Grade Framework' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ==========================================
         STEP 2: SALARY BANDS
         ========================================== -->
    <div v-if="step === 2" class="step-content animate-fade-in">
      
      <div v-if="isLoadingRoles" class="comp-loading">
        <vs-progress indeterminate color="primary"></vs-progress>
        <p>Loading roles and configured bands...</p>
      </div>

      <div v-else>
        <!-- LEVEL TABS -->
        <div class="comp-level-section">
          <label class="comp-section-label">SELECT CAREER LEVEL</label>
          <div class="comp-tabs">
            <button 
              v-for="lvl in activeLevels" 
              :key="lvl"
              @click="selectLevel(lvl)"
              :class="['comp-tab', activeLevelTab === lvl ? 'comp-tab-active' : '']"
            >
              {{ lvl }}
            </button>
          </div>
        </div>

        <div class="comp-layout" v-if="activeLevelTab">
          
          <!-- LEFT: Roles List -->
          <div class="comp-roles-col">
            <div class="comp-box-header" style="margin-bottom: 12px;">
              <h5 style="font-size:14px;">Roles in {{ activeLevelTab }}</h5>
              <span class="comp-count">{{ rolesInActiveLevel.length }} roles</span>
            </div>

            <div class="comp-role-list custom-scroll">
              <div 
                v-for="role in rolesInActiveLevel" :key="role.role_id"
                @click="activeRole = role"
                :class="['comp-role-card', activeRole && activeRole.role_id === role.role_id ? 'comp-role-active' : '']"
              >
                <div class="comp-rc-top">
                  <h6>{{ role.title }}</h6>
                  <span :class="['comp-badge', (role.min_salary || role.max_salary) ? 'comp-badge-configured' : 'comp-badge-unconfigured']">
                    {{ (role.min_salary || role.max_salary) ? 'Configured' : 'Empty' }}
                  </span>
                </div>
                <div class="comp-rc-bottom">
                  <span class="comp-rc-text">{{ formatK(role.min_salary) }}</span>
                  <div class="comp-rc-track" :style="{ background: (role.min_salary || role.max_salary) ? '#F2C244' : '#e2e8f0' }"></div>
                  <span class="comp-rc-text right">{{ formatK(role.max_salary) }}</span>
                </div>
              </div>

              <!-- Empty State -->
              <div v-if="rolesInActiveLevel.length === 0" class="comp-empty-state">
                <p>No roles found in {{ activeLevelTab }}.</p>
                <span>Add roles via the Configuration tab first.</span>
              </div>
            </div>
          </div>

          <!-- RIGHT: Band Configuration -->
          <div class="comp-details-col">
            
            <div v-if="!activeRole" class="comp-empty-panel">
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>
              <p>Select a role from the left to configure its salary band.</p>
            </div>

            <!-- Flex container to prevent leaking -->
            <div v-else class="comp-box" style="height: 100%; display: flex; flex-direction: column; overflow: hidden;">
              
              <div class="comp-box-header mb-6 flex-shrink-0">
                <h5 style="font-size: 18px;">{{ activeRole.title }} · Salary Band</h5>
              </div>

              <!-- Band Input Fields -->
              <div class="band-inputs-grid mb-6 flex-shrink-0">
                <div class="input-group">
                  <label>Minimum Salary</label>
                  <div class="input-with-prefix">
                    <span class="prefix">€</span>
                    <input type="number" v-model.number="activeRole.min_salary" class="comp-input hide-arrows text-lg font-bold" placeholder="e.g. 10000" />
                  </div>
                </div>
                <div class="input-group">
                  <label>Maximum Salary</label>
                  <div class="input-with-prefix">
                    <span class="prefix">€</span>
                    <input type="number" v-model.number="activeRole.max_salary" class="comp-input hide-arrows text-lg font-bold" placeholder="e.g. 20000" />
                  </div>
                </div>
              </div>

              <!-- Visual Slider & Calculated Grades (Scrollable Area) -->
              <div v-if="activeRole.min_salary && activeRole.max_salary" class="flex-grow overflow-y-auto custom-scroll pr-4 mb-2">
                <div class="comp-slider-header mb-2">
                  <span>Band Visualizer</span>
                  <span style="color:#64748b; font-size:11px;">Dots represent Job Grades</span>
                </div>
                
                <div class="comp-slider-track-main mb-2">
                  <div 
                    v-for="grade in grades" :key="'dot-'+grade.id"
                    class="comp-slider-dot-small"
                    :style="{ left: `calc(${grade.position}% - 6px)` }"
                    :title="grade.id + ' (' + grade.position + '%)'"
                  ></div>
                </div>
                <div class="comp-slider-labels mb-8">
                  <span>{{ formatK(activeRole.min_salary) }}</span>
                  <span>{{ formatK((activeRole.min_salary + activeRole.max_salary) / 2) }}</span>
                  <span>{{ formatK(activeRole.max_salary) }}</span>
                </div>

                <h6 class="text-sm font-bold text-gray-800 mb-3">Calculated Salaries by Grade</h6>
                <div class="calc-grid">
                  <div v-for="grade in grades" :key="'calc-'+grade.id" class="calc-row">
                    <div class="calc-left">
                      <span class="calc-grade-id">{{ grade.id }}</span>
                      <span class="calc-desc">{{ grade.position }}% position</span>
                    </div>
                    <div class="calc-right">
                      {{ calculateSalary(activeRole.min_salary, activeRole.max_salary, grade.position) }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Save Button (Sticky at Bottom) -->
              <div class="mt-auto pt-4 border-t border-gray-100 flex justify-end flex-shrink-0">
                <button @click="saveRoleBand" class="comp-btn-primary w-full md:w-auto">
                  <vs-icon icon="save" icon-pack="feather" size="14px" class="mr-2"></vs-icon>
                  {{ isSavingBand ? 'Saving...' : 'Save Role Band' }}
                </button>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: "OrgSettingsCompensation",
  data() {
    return {
      step: 1, // 1=Grades, 2=Bands
      
      // SECTION 7: GRADES (Pre-filled from doc)
      grades: [
        { id: 'G1', minScore: 0.0, position: 20, meaning: 'Entry value within the level' },
        { id: 'G2', minScore: 2.0, position: 40, meaning: 'Developing value within the level' },
        { id: 'G3', minScore: 3.0, position: 58, meaning: 'Standard value for the level' },
        { id: 'G4', minScore: 3.6, position: 75, meaning: 'Above-standard job scope' },
        { id: 'G5', minScore: 4.3, position: 90, meaning: 'High relative job value' },
        { id: 'G6', minScore: 4.7, position: 98, meaning: 'Top of the level\'s job value' },
      ],
      isSavingGrades: false,
      
      // SECTION 8: BANDS
      isLoadingRoles: true,
      allRoleMetadata: [], 
      activeLevelTab: '',
      activeRole: null,
      isSavingBand: false,
    };
  },
  computed: {
    // Added optional chaining fallbacks so it never crashes if state is empty initially
    orgData() { 
      return this.$store.state.organization?.orgData || {}; 
    },
    orgConfig() { 
      return this.$store.state.organization?.organizationConfiguration || {}; 
    },
    activeLevels() {
      const levels = this.orgConfig?.level?.values;
      // CRASH FIX: Ensure it is an array before mapping, and handle both string and object values
      if (Array.isArray(levels)) {
        return levels.map(v => typeof v === 'object' ? (v.label || v.value) : v).filter(Boolean);
      }
      return [];
    },
    rolesInActiveLevel() {
      // Safety check to ensure array exists before filtering
      if (!Array.isArray(this.allRoleMetadata) || !this.activeLevelTab) return [];
      
      return this.allRoleMetadata.filter(role => {
        return role.level === this.activeLevelTab;
      });
    }
  },
  watch: {
    activeLevels: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0 && !this.activeLevelTab) {
          this.activeLevelTab = newVal[0];
        }
      }
    },
    // DATA LOAD FIX: Watch orgData so we fetch exactly when the ID is ready (handles page refresh)
    'orgData._id': {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.fetchEvaluationData();
        }
      }
    }
  },
  methods: {
    // --- STEP 1: GRADES ---
    addGrade() {
      this.grades.push({ id: `G${this.grades.length + 1}`, minScore: 0.0, position: 0, meaning: '' });
    },
    removeGrade(index) {
      this.grades.splice(index, 1);
    },
    async saveGrades() {
      this.isSavingGrades = true;
      try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/v2/organization/${this.orgData._id}/job-grades`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ grades: this.grades })
        });
        
        if (response.ok) {
          this.$vs.notify({ title: 'Success', text: 'Job Grades Framework saved.', color: 'success' });
          this.step = 2; // Move to Bands step
        } else throw new Error("Failed to save");
      } catch (err) {
        this.$vs.notify({ title: 'Error', text: 'Could not save grades.', color: 'danger' });
      } finally {
        this.isSavingGrades = false;
      }
    },

    // --- STEP 2: BANDS ---
    selectLevel(lvl) {
      this.activeLevelTab = lvl;
      this.activeRole = null;
    },
    formatCurrency(value) {
      if (value == null || value === '') return '—';
      return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
    },
    formatK(value) {
      if (value == null || value === '') return '—';
      return '€' + (value / 1000) + 'k';
    },
    calculateSalary(min, max, pos) {
      if (min == null || max == null) return '—';
      const range = max - min;
      const amount = min + (range * (pos / 100));
      return this.formatCurrency(amount);
    },
    
    async saveRoleBand() {
      if (!this.activeRole || !this.orgData._id) return;
      this.isSavingBand = true;
      try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
        const payload = { bands: this.allRoleMetadata };
        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/v2/organization/${this.orgData._id}/salary-bands`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (response.ok) this.$vs.notify({ title: 'Success', text: 'Salary band saved.', color: 'success' });
      } catch (err) { 
        this.$vs.notify({ title: 'Error', text: 'Could not save band.', color: 'danger' }); 
      } finally { 
        this.isSavingBand = false; 
      }
    },
    
    // Unified fetcher for all compensation settings data
    async fetchEvaluationData() {
      if (!this.orgData || !this.orgData._id) return;
      this.isLoadingRoles = true;
      
      try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
        
        // Run both fetches simultaneously for speed
        const [bandRes, gradeRes] = await Promise.all([
          fetch(`${process.env.VUE_APP_API_URL}/api/v2/organization/${this.orgData._id}/salary-bands`, { headers: { 'Authorization': `Bearer ${token}` }}).catch(() => null),
          fetch(`${process.env.VUE_APP_API_URL}/api/v2/organization/${this.orgData._id}/job-grades`, { headers: { 'Authorization': `Bearer ${token}` }}).catch(() => null)
        ]);

        if (bandRes && bandRes.ok) {
          const json = await bandRes.json();
          // Crash fix: ensure it defaults to array
          this.allRoleMetadata = Array.isArray(json.data) ? json.data : [];
        }

        if (gradeRes && gradeRes.ok) {
          const json = await gradeRes.json();
          // Only overwrite grades if the API actually returned a saved configuration
          if (json.data && Array.isArray(json.data) && json.data.length > 0) {
            this.grades = json.data;
          }
        }
      } catch (err) { 
        console.error("Compensation fetch failed", err); 
      } finally { 
        this.isLoadingRoles = false; 
      }
    }
  }
};
</script>

<style scoped>
/* =========================================================
   CLEAN, CUSTOM CSS
   ========================================================= */

.comp-wrapper { padding: 10px 20px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1e293b; }
.comp-header { border-bottom: 1px solid #e2e8f0; padding-bottom: 20px; margin-bottom: 24px; }
.comp-title { font-size: 20px; font-weight: 700; margin: 0 0 4px 0; color: #0f172a; }
.comp-subtitle { font-size: 13px; color: #64748b; margin: 0; }
.comp-desc { font-size: 13px; color: #64748b; line-height: 1.5; }
.comp-loading { text-align: center; padding: 40px; color: #64748b; font-size: 14px; font-weight: 500; }

.animate-fade-in { animation: fadeIn 0.3s ease-in-out; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }

/* === 2-STEP SETUP TABS === */
.setup-tabs { display: flex; gap: 16px; margin-bottom: 24px; }
.setup-tab { flex: 1; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; display: flex; align-items: center; gap: 16px; background: #ffffff; cursor: pointer; transition: all 0.2s; }
.setup-tab:hover { background: #f8fafc; border-color: #cbd5e1; }
.setup-tab.active { background: #fffbeb; border-color: #F2C244; box-shadow: 0 2px 8px rgba(242, 194, 68, 0.15); }

.step-num { width: 32px; height: 32px; border-radius: 50%; background: #f1f5f9; color: #475569; font-weight: 700; font-size: 14px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.2s; }
.setup-tab.active .step-num { background: #F2C244; color: #0f172a; }

.step-text { display: flex; flex-direction: column; text-align: left; }
.step-text strong { font-size: 14px; color: #0f172a; margin-bottom: 2px; }
.step-text span { font-size: 11px; color: #64748b; }
.setup-tab .step-text {
  text-align: left !important;
}

/* === SHARED COMPONENTS === */
.comp-box { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 24px; box-shadow: 0 1px 3px rgba(0,0,0,0.02); }
.comp-box-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.comp-box-header h5 { margin: 0; font-size: 16px; font-weight: 700; color: #1e293b; }
.comp-badge-green { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; }
.comp-badge-red { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; }

/* Buttons */
.comp-btn-primary { background: #F2C244; color: #0f172a; border: none; padding: 10px 20px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; justify-content: center; }
.comp-btn-primary:hover:not(:disabled) { background: #eab308; }
.comp-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; background: #e2e8f0; color: #94a3b8; }
.comp-btn-outline { background: transparent; border: 1px solid #cbd5e1; color: #475569; padding: 10px 20px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.comp-btn-outline:hover { background: #f8fafc; border-color: #94a3b8; }
.comp-del-btn { background: transparent; border: none; color: #cbd5e1; cursor: pointer; padding: 8px; border-radius: 4px; transition: all 0.2s; }
.comp-del-btn:hover { color: #ef4444; background: #fef2f2; }

/* Forms & Inputs */
.comp-input { width: 100%; border: 1px solid #cbd5e1; border-radius: 6px; padding: 8px 12px; font-size: 13px; color: #1e293b; outline: none; transition: border 0.2s; }
.comp-input:focus { border-color: #F2C244; }
.w-full { width: 100%; box-sizing: border-box; }
.hide-arrows::-webkit-outer-spin-button, .hide-arrows::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.font-semibold { font-weight: 600; }
.font-bold { font-weight: 700; }
.text-lg { font-size: 1.125rem; }

.input-with-suffix, .input-with-prefix { position: relative; display: flex; align-items: center; }
.input-with-suffix .suffix { position: absolute; right: 12px; color: #94a3b8; font-size: 12px; font-weight: 600; }
.input-with-prefix .prefix { position: absolute; left: 12px; color: #64748b; font-size: 14px; font-weight: 700; }
.input-with-prefix .comp-input { padding-left: 28px; }

/* === TABLES === */
.grades-table-wrapper { overflow-x: auto; border: 1px solid #e2e8f0; border-radius: 8px; }
.grades-table { width: 100%; border-collapse: collapse; text-align: left; }
.grades-table th { background: #f8fafc; padding: 12px 16px; font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; border-bottom: 1px solid #e2e8f0; }
.grades-table td { padding: 12px 16px; border-bottom: 1px solid #e2e8f0; vertical-align: middle; }
.grades-table tr:last-child td { border-bottom: none; }

/* === STEP 2: BANDS & LAYOUT === */
.comp-level-section { margin-bottom: 24px; }
.comp-section-label { display: block; font-size: 10px; font-weight: 700; color: #94a3b8; letter-spacing: 0.05em; margin-bottom: 8px; text-transform: uppercase; }
.comp-tabs { display: flex; flex-wrap: wrap; gap: 8px; }
.comp-tab { background: #ffffff; border: 1px solid #cbd5e1; color: #475569; padding: 6px 16px; border-radius: 50px; font-size: 13px; cursor: pointer; transition: all 0.2s; font-weight: 500; }
.comp-tab:hover { border-color: #94a3b8; color: #1e293b; }
.comp-tab-active { background: #F2C244 !important; border-color: #F2C244 !important; color: #111827 !important; font-weight: 600; }

.comp-layout { display: flex; gap: 24px; align-items: stretch; height: 500px; }
.comp-roles-col { width: 320px; flex-shrink: 0; display: flex; flex-direction: column; }
.comp-details-col { flex: 1; min-width: 0; }
@media (max-width: 1024px) { .comp-layout { flex-direction: column; height: auto; } .comp-roles-col { width: 100%; } }

.comp-role-list { display: flex; flex-direction: column; gap: 12px; overflow-y: auto; padding-right: 4px; flex-grow: 1; }
.custom-scroll::-webkit-scrollbar { width: 6px; }
.custom-scroll::-webkit-scrollbar-track { background: transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 10px; }

.comp-role-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; cursor: pointer; transition: all 0.2s; }
.comp-role-card:hover { border-color: #cbd5e1; background: #f8fafc; }
.comp-role-active { border-color: #F2C244; background: #fffbeb; }
.comp-rc-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.comp-rc-top h6 { margin: 0; font-size: 13px; font-weight: 700; color: #0f172a; }
.comp-badge { font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: 4px; border: 1px solid transparent; }
.comp-badge-configured { background: #fef9c3; color: #854d0e; border-color: #fde047; }
.comp-badge-unconfigured { background: #f8fafc; color: #64748b; border-color: #cbd5e1; }
.comp-rc-bottom { display: flex; align-items: center; gap: 8px; }
.comp-rc-text { font-size: 11px; font-weight: 600; color: #64748b; min-width: 24px; }
.comp-rc-text.right { text-align: right; }
.comp-rc-track { flex-grow: 1; height: 4px; border-radius: 4px; }

.comp-empty-panel { background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 12px; padding: 60px 20px; text-align: center; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; }
.comp-empty-panel p { margin: 16px 0 0 0; font-size: 14px; font-weight: 500; color: #64748b; }

/* Band Details Right Column */
.band-inputs-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.input-group label { display: block; font-size: 11px; font-weight: 600; color: #64748b; margin-bottom: 6px; text-transform: uppercase; }

.comp-slider-header { display: flex; justify-content: space-between; align-items: center; font-size: 13px; font-weight: 600; color: #1e293b; }
.comp-slider-track-main { height: 6px; background: #F2C244; border-radius: 10px; position: relative; width: 100%; border: 1px solid #1e293b; }
.comp-slider-dot-small { position: absolute; top: 50%; width: 12px; height: 12px; background: #ffffff; border: 2px solid #1e293b; border-radius: 50%; transform: translateY(-50%); box-shadow: 0 1px 2px rgba(0,0,0,0.2); }
.comp-slider-labels { display: flex; justify-content: space-between; font-size: 11px; font-weight: 600; color: #94a3b8; }

/* Calculation Table */
.calc-grid { display: flex; flex-direction: column; gap: 8px;  }
.calc-row { display: flex; justify-content: space-between; align-items: center; background: #f8fafc; border: 1px solid #e2e8f0; padding: 10px 16px; border-radius: 8px; }
.calc-left { display: flex; flex-direction: column; }
.calc-grade-id { font-size: 13px; font-weight: 700; color: #0f172a; }
.calc-desc { font-size: 10px; color: #64748b; }
.calc-right { font-size: 14px; font-weight: 700; color: #15803d; }
</style>